import type { InputV1, MoveV1 } from '@gonzovnc/protocol';
import type { PointerIntent, KeyIntent, PointerButton } from './types.js';

export interface Outgoing {
  topic: 'input.v1' | 'move.v1';
  message: InputV1 | MoveV1;
}

/**
 * Pure, DOM-free construction of input.v1 / move.v1 messages (§6.2-6.3). Owns the
 * control epoch, the per-epoch input.v1 seq counter, the independent move.v1 seq
 * counter, and the set of currently-held mouse buttons (so a move becomes an mdrag).
 * The Host's dead-man rule fences everything; this side just gets the wire right.
 */
export class InputEncoder {
  private epoch = 0;
  private inputSeq = 0;
  private moveSeq = 0;
  private heldButtons = new Set<PointerButton>();

  /** Called when the Host `hello` arrives (or attributes change to a new epoch). */
  setEpoch(epoch: number): void {
    this.epoch = epoch;
    this.inputSeq = 0; // per-epoch seq restarts; first message is seq 1
    this.heldButtons.clear();
  }

  get currentEpoch(): number { return this.epoch; }
  get anyButtonHeld(): boolean { return this.heldButtons.size > 0; }

  private nextInputSeq(): number { return ++this.inputSeq; }
  private nextMoveSeq(): number { return this.moveSeq++; }

  private env(t: string): { v: 1; e: number; seq: number; t: string } {
    return { v: 1, e: this.epoch, seq: this.nextInputSeq(), t };
  }

  pointerDown(p: PointerIntent, btn: PointerButton, clicks: number): Outgoing {
    this.heldButtons.add(btn);
    return { topic: 'input.v1', message: { ...this.env('mdown'), disp: p.disp, x: p.x, y: p.y, btn, clicks: Math.max(1, Math.min(3, clicks)) } as InputV1 };
  }

  pointerUp(p: PointerIntent, btn: PointerButton): Outgoing {
    this.heldButtons.delete(btn);
    return { topic: 'input.v1', message: { ...this.env('mup'), disp: p.disp, x: p.x, y: p.y, btn } as InputV1 };
  }

  /** Hover move (no button) -> lossy move.v1; drag (button held) -> reliable mdrag. */
  pointerMove(p: PointerIntent): Outgoing {
    if (this.anyButtonHeld) {
      return { topic: 'input.v1', message: { ...this.env('mdrag'), disp: p.disp, x: p.x, y: p.y } as InputV1 };
    }
    return { topic: 'move.v1', message: { v: 1, seq: this.nextMoveSeq(), disp: p.disp, x: p.x, y: p.y } as MoveV1 };
  }

  wheel(p: PointerIntent, dx: number, dy: number, mods: number): Outgoing {
    return { topic: 'input.v1', message: { ...this.env('wheel'), disp: p.disp, x: p.x, y: p.y, dx, dy, mods } as InputV1 };
  }

  keyDown(k: KeyIntent): Outgoing {
    return { topic: 'input.v1', message: { ...this.env('kdown'), code: k.code, key: k.key, mods: k.mods, rep: k.repeat } as InputV1 };
  }

  keyUp(code: string, mods: number): Outgoing {
    return { topic: 'input.v1', message: { ...this.env('kup'), code, mods } as InputV1 };
  }

  text(str: string): Outgoing {
    return { topic: 'input.v1', message: { ...this.env('text'), str } as InputV1 };
  }

  displaySwitch(disp: string): Outgoing {
    return { topic: 'input.v1', message: { ...this.env('dswitch'), disp } as InputV1 };
  }

  /** Release everything: sent on blur/visibility loss/revoke/reconnect (§6.3). */
  reset(): Outgoing {
    this.heldButtons.clear();
    return { topic: 'input.v1', message: { ...this.env('reset') } as InputV1 };
  }

  ping(ts: number): Outgoing {
    return { topic: 'input.v1', message: { ...this.env('ping'), ts } as InputV1 };
  }
}
